import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { apiConnector } from '../Services/ApiConnector';
import { authEndPoints } from '../Services/AllApi';
import { FaSpinner, FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaMapMarkerAlt, FaEdit, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import usePageTitle from '../hooks/usePageTitle';

const Profile = () => {
  usePageTitle('Profile');
  
 // const { token, user } = useSelector((state) => state.auth);
  const { token} = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [error, setError] = useState(null);

  // Redirect to login if no token
  useEffect(() => {
    if (!token) {
      navigate('/auth', { replace: true });
      return;
    }
  }, [token, navigate]);

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get user ID and token from localStorage
        const userId = localStorage.getItem('user_id');
        const tokenFromStorage = localStorage.getItem('token');
        
        if (!userId) {
          throw new Error('User ID not found. Please login again.');
        }
        
        if (!tokenFromStorage) {
          throw new Error('Token not found. Please login again.');
        }
        
        console.log('Fetching user details with:');
        console.log('User ID:', userId);
        console.log('Token:', tokenFromStorage ? `${tokenFromStorage.substring(0, 20)}...` : 'No token');
        console.log('API URL:', `${authEndPoints.GET_USER_API}?user_id=${userId}`);
        
        // Use the GET_USER_API endpoint with user_id parameter and proper headers
        const response = await apiConnector('GET', `${authEndPoints.GET_USER_API}?user_id=${userId}`, null, {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenFromStorage}`,
        });

        console.log('User details response:', response);
        
        if (response && response.data) {
          setUserDetails(response.data);
          // Update Redux state with fresh user data
          dispatch({ type: 'auth/setUser', payload: response.data });
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setError('Failed to fetch user details');
        
        // If token is invalid/expired, redirect to login
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('token_type');
          localStorage.removeItem('expires_in');
          dispatch({ type: 'auth/setToken', payload: null });
          dispatch({ type: 'auth/setUser', payload: null });
          toast.error('Session expired. Please login again.');
          navigate('/auth', { replace: true });
        } else if (error.message.includes('User ID not found')) {
          toast.error('Session expired. Please login again.');
          navigate('/auth', { replace: true });
        } else {
          toast.error('Failed to load profile. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [dispatch, navigate]);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleEditProfile = () => {
    // TODO: Implement edit profile functionality
    toast.info('Edit profile functionality coming soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error Loading Profile</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleBackToHome}
                className="bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-600 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b-2 border-orange-200">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={handleBackToHome}
            className="flex items-center space-x-3 text-orange-800 hover:text-orange-600 transition-colors"
          >
            <FaArrowLeft className="text-xl" />
            <span className="text-lg font-semibold">Back to Home</span>
          </button>
          <h1 className="text-2xl font-bold text-orange-800">Profile</h1>
          <button
            onClick={handleEditProfile}
            className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            <FaEdit />
            <span>Edit</span>
          </button>
        </div>
      </div>

      {/* Profile Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Profile Avatar */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  {userDetails?.image ? (
                    <img
                      src={userDetails.image}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span>
                      {userDetails?.name ? userDetails.name.charAt(0).toUpperCase() : 
                       userDetails?.firstName ? userDetails.firstName.charAt(0).toUpperCase() : 
                       userDetails?.email ? userDetails.email.charAt(0).toUpperCase() : 'U'}
                    </span>
                  )}
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  {userDetails?.name || userDetails?.firstName || 'User'}
                </h2>
                <p className="text-gray-600 text-lg mb-4">
                  {userDetails?.email || 'No email provided'}
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {userDetails?.phone && (
                    <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                      📱 {userDetails.phone}
                    </span>
                  )}
                  {userDetails?.role && (
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      👤 {userDetails.role}
                    </span>
                  )}
                  {userDetails?.status && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      userDetails.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {userDetails.status === 'active' ? '✅ Active' : '⏸️ Inactive'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* User Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaUser className="mr-2 text-orange-500" />
                Personal Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <FaEnvelope className="text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{userDetails?.email || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FaPhone className="text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{userDetails?.phone || 'Not provided'}</p>
                  </div>
                </div>
                {userDetails?.dateOfBirth && (
                  <div className="flex items-center">
                    <FaCalendarAlt className="text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Date of Birth</p>
                      <p className="font-medium">{new Date(userDetails.dateOfBirth).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
                {userDetails?.address && (
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-medium">{userDetails.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Account Information */}
            {/* <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaUser className="mr-2 text-orange-500" />
                Account Information
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">User ID</p>
                  <p className="font-medium text-sm font-mono bg-gray-100 p-2 rounded">
                    {userDetails?.id || userDetails?.user_id || 'Not available'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Account Status</p>
                  <p className="font-medium">
                    <span className={`px-2 py-1 rounded text-sm ${
                      userDetails?.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {userDetails?.status || 'Unknown'}
                    </span>
                  </p>
                </div>
                {userDetails?.createdAt && (
                  <div>
                    <p className="text-sm text-gray-500">Member Since</p>
                    <p className="font-medium">{new Date(userDetails.createdAt).toLocaleDateString()}</p>
                  </div>
                )}
                {userDetails?.lastLogin && (
                  <div>
                    <p className="text-sm text-gray-500">Last Login</p>
                    <p className="font-medium">{new Date(userDetails.lastLogin).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div> */}
          </div>

          
        </div>
      </div>
    </div>
  );
};

export default Profile;
